import { useEffect, useState } from 'react';

import useGetCompanyNumbers from './setupProgress/queries/useGetCompanyNumbers';
import useGetCompanyUsers from './setupProgress/queries/useGetCompanyUsers';
import useGetRecentCallRecords from './setupProgress/queries/useGetRecentCallRecords';
import useGetAvailableServiceProfiles from './setupProgress/queries/useGetAvailableServiceProfiles';
import useGetAvailableNumberBlocks from './setupProgress/queries/useGetAvailableNumberBlocks';
import useGetCompanyTrunks from './queries/useGetCompanyTrunks';
import useGetCompanyAdminUsers from './notifications/queries/useGetCompanyAdminUsers';
import useGetCompanyApiUsers from './notifications/queries/useGetCompanyApiUsers';
import useGetCompanyTrsRegistrations from './notifications/queries/useGetCompanyTrsRegistrations';

/**
 * (2635) Whether every request the dashboard home's sections open with has
 * settled, so the page shows once instead of each section popping in on its
 * own. Uses the sections' own queries, so nothing is fetched twice.
 *
 * Failed requests count as settled; the sections already handle them.
 *
 * Once ready it stays ready for that company: background refetches, polling
 * and returning from the service wizard never bring the loader back, the
 * sections update on their own. Only switching company starts over.
 *
 * @param {object} params
 * @param {string} params.companyId
 * @param {boolean} params.isServicesPending - the dashboard payload has not landed yet
 * @param {boolean} params.isCompanyPending - /company/{id} has not landed yet
 * @returns {boolean}
 */
const useDashboardHomeReady = ({
    companyId,
    isServicesPending,
    isCompanyPending,
}) => {
    const queries = [
        useGetCompanyNumbers(companyId),
        useGetCompanyUsers(companyId),
        useGetRecentCallRecords(companyId),
        useGetAvailableServiceProfiles(companyId),
        useGetAvailableNumberBlocks(companyId),
        useGetCompanyTrunks(companyId),
        useGetCompanyAdminUsers(companyId),
        useGetCompanyApiUsers(companyId),
        useGetCompanyTrsRegistrations(companyId),
    ];

    // isInitialLoading rather than isLoading: a disabled query reports
    // isLoading forever in React Query v4.
    const isSettled =
        !isServicesPending &&
        !isCompanyPending &&
        queries.every((query) => !query.isInitialLoading);

    const [readyCompanyId, setReadyCompanyId] = useState(null);
    useEffect(() => {
        if (isSettled) setReadyCompanyId(companyId);
    }, [isSettled, companyId]);

    return isSettled || readyCompanyId === companyId;
};

export default useDashboardHomeReady;
